/**
 * How the slides print the document (docs/strategy-slides-binding-instructions.md
 * §6): the labels for coded values, the number and date helpers, and the
 * display rules every template follows. Slide-only: the rest of the app keeps
 * its own labels ("Reels" in the calendar) and `pct` with one decimal.
 */
import { platformLabel } from "../../components/PlatformGlyph";

const WORDS = ["No", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten", "Eleven", "Twelve"];

/** The text's first letter capitalised; the rest stays as written (§6.4 rule 1). */
export const capFirst = (text: string) => text.replace(/^(\s*["“‘'(]?)(\p{Ll})/u, (_, lead: string, letter: string) => lead + letter.toUpperCase());

/** "Any code" → "Any code": underscores to spaces, first letter capitalised. */
const generic = (code: string) => capFirst(code.replace(/_/g, " ").trim());

const FORMATS: Record<string, string> = { reel: "Reel", carousel: "Carousel", static: "Static", text: "Text" };
const CODES: Record<string, string> = {
  rising: "Rising", heavy: "heavy", standard: "standard", lighter: "lighter",
  internal_benchmark: "Internal benchmark", scrape: "Scrape",
  own: "We own it", participate: "We join in",
};
const STATUS: Record<string, string> = { active: "Live" };

/** The label a coded value shows (§6.2). Platforms are uppercase codes. */
export function label(code: string | null | undefined): string {
  const value = (code ?? "").trim();
  if (!value) return "";
  if (/^[A-Z_]+$/.test(value)) return platformLabel(value) === value ? generic(value.toLowerCase()) : platformLabel(value);
  return FORMATS[value.toLowerCase()] ?? CODES[value] ?? generic(value);
}
export const statusLabel = (status: string) => STATUS[status] ?? generic(status);

/** 950 → "950" · 45000 → "45K" · 2100000 → "2.1M". */
const COMPACT = new Intl.NumberFormat("en", { notation: "compact", maximumFractionDigits: 1 });
export const compact = (n: number) => COMPACT.format(n);
/** A rounded whole number: 64.29 → "64". */
export const wholePct = (n: number) => (Number.isFinite(n) ? String(Math.round(n)) : "0");

function parse(value: string | null | undefined): Date | null {
  if (!value) return null;
  const date = new Date(value.length === 10 ? `${value}T00:00:00Z` : value);
  return Number.isNaN(date.getTime()) ? null : date;
}
const fmt = (options: Intl.DateTimeFormatOptions) => {
  const f = new Intl.DateTimeFormat("en-GB", { ...options, timeZone: "UTC" });
  return (value: string | null | undefined) => { const d = parse(value); return d ? f.format(d) : ""; };
};
/** "November 2026". */
export const monthYear = fmt({ month: "long", year: "numeric" });
/** "15 November". */
export const dayMonth = fmt({ day: "numeric", month: "long" });
/** "1 August 2026". */
export const dayMonthYear = fmt({ day: "numeric", month: "long", year: "numeric" });
/** "November". */
export const monthName = fmt({ month: "long" });
/** "2026 to 2027", or "2026" inside one year. */
export function years(start?: string | null, end?: string | null): string {
  const from = start?.slice(0, 4); const to = end?.slice(0, 4);
  if (!from || !to) return "";
  return from === to ? from : `${from} to ${to}`;
}

/** "Three", for a title that counts what the document holds; 13 and up are digits. */
export const countWord = (n: number) => WORDS[n] ?? String(n);
/** "A and B", "A, B and C". */
export const joinAnd = (items: string[]) => (items.length < 2 ? items.join("") : `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`);
/** Wrapped in “ ” unless it already opens with a quote mark. */
export const quoted = (text: string) => (/^["“«]/.test(text.trim()) ? text.trim() : `“${text.trim()}”`);
/** A full stop when the text doesn't already end a sentence (§6.4 rule 2). */
export const sentence = (text: string) => { const t = text.trim(); return !t || /[.!?…]["”’)]?$/.test(t) ? t : `${t}.`; };
/** A timeframe that reads on after a comma: "End of Q2 2027" → "end of Q2 2027". */
export const afterComma = (text: string) => text.trim().replace(/^(Within|In|By|Over|For|After|Before|During|Across|Until|End)(?=\s)/, word => word.toLowerCase());
/** A name joined to what follows, without doubled punctuation. */
export const bare = (text: string) => text.trim().replace(/[.,;:\s]+$/, "");

/** An http(s) address, or nothing. */
export function sourceUrl(value?: string | null): string | undefined {
  if (!value) return undefined;
  try { const url = new URL(value); return ["https:", "http:"].includes(url.protocol) ? url.href : undefined; }
  catch { return undefined; }
}

/**
 * The highlight on a title that comes from a field (§6.4 rule 3): the text
 * after the first break (", " ": " ". " before the end), else the last three
 * words. A fixed title names its own phrase instead.
 */
export function highlightOf(title: string): string | undefined {
  const t = title.trim();
  const brk = /[,:.]\s+(?=\S)/.exec(t);
  if (brk && brk.index > 0) return t.slice(brk.index + brk[0].length);
  const words = t.split(/\s+/);
  return words.length > 3 ? words.slice(-3).join(" ") : undefined;
}

/**
 * Split a list across slides evenly (§4 "repeat"): ⌈count ÷ n⌉ slides, the
 * earlier ones taking the extra item, so 4 → 2 + 2, 5 → 3 + 2, 7 → 3 + 2 + 2.
 */
export function spread<T>(items: T[], perSlide: number): T[][] {
  if (!items.length) return [];
  const slides = Math.ceil(items.length / perSlide);
  const base = Math.floor(items.length / slides);
  const extra = items.length % slides;
  const out: T[][] = [];
  let at = 0;
  for (let i = 0; i < slides; i++) {
    const size = base + (i < extra ? 1 : 0);
    out.push(items.slice(at, at + size));
    at += size;
  }
  return out;
}
